/**
 * @module app.shared
 */
import { Container } from 'aurelia-framework';

/**
 * Replaces an element with an identical element with a different tag name.
 * @param {Element} el - The element to replace.
 * @param {string} tag - The tag name for the replacement element.
 * @returns {Element} - The replacement element.
 */
export function changeTag(el: Element, tag: string): Element {
    const newEl = document.createElement(tag);
    Array.from(el.childNodes).forEach((node) => newEl.appendChild(node));
    Array.from(el.attributes).forEach(({ name, value }) => newEl.setAttribute(name, value));
    (<any>el).replaceWith(newEl);
    return newEl;
}

/**
 * Specifies a named slot for all child elements matching a given element.
 * @param {Element} element - The parent element.
 * @param {string} selector - The CSS selector to match child elements against.
 * @param {string} slot - The slot name to assign to matching child elements.
 */
export function moveToSlot(element: Element, selector: string, slot: string) {
    for(let rule of element.querySelectorAll(selector)) {
        rule.setAttribute('slot', slot);
    }
}

/**
 * Transforms a function of a single Element into a valid argument for @processContent.
 * @param {(el: Element) => any;} fn - The function to transform.
 * @returns {(_a: any, _b: any, element: Element, _d: any) => true} - fn as a valid argument for @processContent.
 */
export function asContentProcessor(fn: (el: Element) => any): (_a: any, _b: any, element: Element, _d: any) => true {
    return function processContent(_a: any, _b: any, element: Element, _d: any): true {
        fn(element);
        return true;
    }
}

/**
 * Finds the closest element attached to the DOM using Aurelia DI containers.
 * Specifically created for firing an event in the detached() callback.
 * @param container - The DI container to begin searching from.
 */
export function findClosestAttachedElement(container: Container): HTMLElement | void {

    // Search through every container and parent container...
    while(container) {

        // ...for a view slot. If it is exists...
        let el;
        if(Reflect.has(container, 'viewSlot')) {
            el = Reflect.get(container, 'viewSlot').anchor;
        }

        // ...and it is attached to the DOM root...
        if(el && el.isConnected) {

            // return the closest HTMLElement
            while(!(el instanceof HTMLElement)) {
                el = el.parentElement;
            }
            return el;
        }

        container = container.parent;
    }
}

/**
 * Remove all immediate child `TEXT_NODE` nodes from the specified DOM element
 * @param element the element to remove text nodes from
 */
export function removeTextNodes(element: Element): boolean {
    let result = false;
    for(const child of Array.from(element.childNodes)) {
        if(child.nodeType === Node.TEXT_NODE) {
            element.removeChild(child);
            result = true;
        }
    }
    return result;
}

/**
 *  * Prepends a child `TEXT_NODE` node to the beginning of the specified DOM element
 * @param element the element to insert the text node into
 * @param text the text contents of the text node
 */
export function prependTextNode(element: Element, text: string): Text {
    const textNode = (element.ownerDocument || window.document).createTextNode(text);
    return element.childNodes[0].insertBefore(textNode, element.childNodes.length === 0 ? null : element.childNodes[0]);
}

/**
 * Highlights text in an element.
 */
export function selectText(element: Node) {
    const selection = window.getSelection();
    if(selection !== null) {
        const range = document.createRange();
        range.selectNodeContents(element);
        selection.removeAllRanges();
        selection.addRange(range);
    }
}

export function clearSelection() {
    const selection = window.getSelection();
    if(selection !== null) {
        selection.empty();
    }
}

export function removeChildren(element: Element): void {
    while(element.firstChild) {
        if(element.lastChild) {
            element.removeChild(element.lastChild);
        }
    }
}

export type ElementAction = (element: Element) => void;

export function forEachSibling(source: Element, action: ElementAction): void {
    var parent = source.parentElement;
    if(!parent) { return; }

    var sibling = parent.firstChild;
    while(sibling) {
        if(sibling.nodeType === 1 && sibling !== source && sibling instanceof Element) {
            action(sibling);
        }
        sibling = sibling.nextSibling;
    }
}

export function forEachAncestor(source: Element, action: ElementAction): void {
    let current = source.parentElement;
    while(current != null) {
        action(current);
        current = current.parentElement;
    }
}

export function forEachAncestorAndSelf(source: Element, action: ElementAction): void {
    let current: Element | null = source;
    while(current != null) {
        action(current);
        current = current.parentElement;
    }
}

export function swapElements(e1: Element, e2: Element) {
    let placeholder: Element;
    let place: InsertPosition;
    if(e1.previousElementSibling) {
        placeholder = e1.previousElementSibling;
        place = 'afterend';
    } else {
        placeholder = e1.nextElementSibling!;

        place = 'beforebegin';
    }
    e2.insertAdjacentElement('afterend', e1);
    placeholder?.insertAdjacentElement(place, e2);
}

/**
 * Given an iterable list of Elements and a numerical order, reorders the elements consecutively in the given order.
 * @note Inserts each subsequent element immediately after the previous element.
 * @param elements The list of Elements to reorder.
 * @param order An ordered list corresponding to the current index in the elements list.
 * @example reorderElements([<a>,<b>,<c>], [3,2,1]) would result in <c><b><a> wherever <c> is located in the DOM.
 */
export function reorderElements(elements: Iterable<Element>, order: number[]) {
    if(!Array.isArray(elements)) {
        elements = Array.from(elements);
    }
    const ordered = order.map((i) => (elements as Element[])[i]).filter(el => el !== undefined);

    // INFO [Matt] Inserts each n+1st element (element) "after the end" of each nth element (ordered[index]).
    ordered.slice(1).forEach((element, index) => ordered[index].insertAdjacentElement('afterend', element));
}

/**
 * Sets a transition animation on an element that runs only once.
 * @param element The element to animate
 * @param properties A record of css keys to css values to animate
 * @param timing Optionally, the duration and easing function for the animation
 */
export function animateOnce(element: HTMLElement, properties: Partial<CSSStyleDeclaration>, timing: string = '0.15s ease-in-out') {
    const [, value, unit] = /([0-9\.]+)([ms]+)/.exec(timing) || [];
    const timeout = parseFloat(value) * (unit === 's' ? 1000 : 1);
    return new Promise<void>((resolve) => {
        element.addEventListener('transitionend', () => resolve(), { once: true });
        element.style.transition = Reflect.ownKeys(properties).map((property) => `${String(property)} ${timing}`).join(', ');
        for(let property in properties) {
            element.style[property] = properties[property]!;
        }
        setTimeout(resolve, timeout || 0);
    }).then(() => element.style.transition = '');
}

/**
 * Returns true when the given element has scrolled to the bottom of its scrollable container,
 * allowing 1 pixel of leeway for rounding errors.
 */
export function isScrolledToBottom({ scrollHeight, offsetHeight, scrollTop }: HTMLElement) {
    return Math.abs(scrollHeight - offsetHeight - scrollTop) <= 1;
}

export function getClosestBoundary(element: HTMLElement): HTMLElement {
    return element.closest('.ess-boundary') || document.documentElement;
}

export function getParentByClassName(element: HTMLElement, className: string): HTMLElement | null {
    while(!element.classList.contains(className) && element.parentElement) {
        element = element.parentElement;
    }

    return element.classList.contains(className) ? element : null;
}

/**
 * Sets an element's attribute to the value if the attribute is not currently set
 * @param element
 * @param attributeName
 * @param value
 */
export function setAttributeIfNotExist(element: Element, attributeName: string, value: string): void {
    if(!element.hasAttribute(attributeName)) {
        return element.setAttribute(attributeName, value);
    }
}

/**
 * Sets an element's attributes to a value if the attribute is not currently set
 * @param element
 * @param attributes An indexed object of attribute name/value pairs
 */
export function setAttributesIfNotExist(element: Element, attributes: { [name: string]: string }): void {
    Object.keys(attributes).forEach((attribute) => setAttributeIfNotExist(element, attribute, attributes[attribute]));
}

/**
 * Is the element interactive according to
 * https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Content_categories#interactive_content
 * @param element The element to check
 * @returns {boolean} Whether the element is interactive
 */
export function isElementInteractive(element: HTMLElement): boolean {
    const BASIC_INTERACTIVE_ELEMENTS = ['A', 'BUTTON', 'DETAILS', 'EMBED', 'IFRAME', 'KEYGEN', 'SELECT', 'TEXTAREA'];
    if(BASIC_INTERACTIVE_ELEMENTS.includes(element.tagName)) { return true; }

    // Consider <label> interactive only when it has control over another element
    if(element.tagName === 'LABEL') {
        const labelElement = element as HTMLLabelElement;
        return !labelElement.control === null;
    }

    if(element.tagName === 'INPUT' && element.getAttribute('type') !== 'hidden') { return true; }
    if((element.tagName === 'IMG' || element.tagName === 'OBJECT') && element.getAttribute('useMap')) { return true; }
    if((element.tagName === 'AUDIO' || element.tagName === 'VIDEO') && element.hasAttribute('controls')) { return true; }

    return false;
}

/**
 * Check if an element has clickable interactivity
 * @param element The element to check
 * @returns Whether the element is clickable
 */
export function isElementClickable(element: HTMLElement): boolean {
    return element.tagName === 'A' ||
        element.hasAttribute('click.delegate') ||
        element.hasAttribute('click.trigger') ||
        element.hasAttribute('ess-focusable');
}

